/**
 * A safe wrapper around `requestIdleCallback` that falls back to `requestAnimationFrame`
 * if `requestIdleCallback` is not supported by the browser. This function schedules
 * a callback to be executed during an idle period or using an animation frame as a fallback.
 *
 * @param cb - The callback function to execute. It receives either an `IdleDeadline` object
 * or a timestamp (number) depending on the internal method used.
 * @param options - An optional object containing configuration options for `requestIdleCallback`.
 * This parameter is ignored if `requestAnimationFrame` is used.
 * @returns A unique identifier for the callback. The identifier can be used to cancel the scheduled
 * callback using `cancelIdleCallback` or `cancelAnimationFrame`.
 */
export const safeRequestIdleCallback = (
  cb: (deadlineOrTime: IdleDeadline | number) => void,
  options?: IdleRequestOptions,
): number => {
  if ('requestIdleCallback' in window) {
    return window.requestIdleCallback(cb as IdleRequestCallback, options)
  } else {
    return requestAnimationFrame(cb as FrameRequestCallback)
  }
}
